import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { t } from '../utils/localization/localization';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';
import {
  Container,
  Box,
  Paper,
  Tabs,
  Tab,
  Typography
} from '@mui/material';
import UserManagement from '../components/admin/UserManagement';
import RoleManagement from '../components/admin/RoleManagement';
import SystemLanguageManagement from '../components/admin/SystemLanguageManagement';
import DepartmentManagement from '../components/admin/DepartmentManagement';
import SkillManagement from '../components/admin/SkillManagement';
import ReprocessingManagement from '../components/admin/ReprocessingManagement';

export default function AdminDashboardPage() {
  const location = useLocation();
  const path = location.pathname;

  // Get language context and use custom hook for language-aware rendering
  const languageRenderKey = useLanguageAwareRender('AdminDashboardPage');

  // Determine which tab is active
  const getTabValue = () => {
    if (path.includes('/admin/departments')) return 1;
    if (path.includes('/admin/skills')) return 2;
    if (path.includes('/admin/roles')) return 3;
    if (path.includes('/admin/system-language')) return 4;
    if (path.includes('/admin/reprocessing')) return 5;
    return 0; // Default to users tab
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: 4,
        mb: 4,
        px: 3
      }}
      key={languageRenderKey}
    >
      <Box>
        <Typography variant="h4" gutterBottom>
          {t('admin.dashboard')}
        </Typography>

        <Paper sx={{
          mb: 4,
          width: '100%',
          overflow: 'visible',
          boxSizing: 'border-box'
        }}>
          <Tabs
            value={getTabValue()}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              width: '100%',
              '& .MuiTabs-scroller': {
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none'
              },
              '& .MuiTabs-flexContainer': {
                justifyContent: 'flex-start',
                paddingLeft: '16px',
                paddingRight: '16px',
                gap: '8px'
              },
              '& .MuiTab-root': {
                minWidth: '140px',
                maxWidth: '320px',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
                padding: '12px 16px',
                flexShrink: 0,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              },
              '& .MuiTabs-scrollButtons': {
                width: '40px',
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }
            }}
          >
            <Tab label={t('admin.userManagement')} component={Link} to="/admin" />
            <Tab label={t('admin.departmentManagement')} component={Link} to="/admin/departments" />
            <Tab label={t('admin.skillManagement')} component={Link} to="/admin/skills" />
            <Tab label={t('admin.roleManagement')} component={Link} to="/admin/roles" />
            <Tab label={t('admin.systemLanguageManagement')} component={Link} to="/admin/system-language" />
            <Tab label={t('admin.reprocessingManagement')} component={Link} to="/admin/reprocessing" />
          </Tabs>
        </Paper>

        <Routes>
          <Route path="/" element={<UserManagement />} />
          <Route path="/departments" element={<DepartmentManagement />} />
          <Route path="/skills" element={<SkillManagement />} />
          <Route path="/roles" element={<RoleManagement />} />
          <Route path="/system-language" element={<SystemLanguageManagement />} />
          <Route path="/reprocessing" element={<ReprocessingManagement />} />
        </Routes>
      </Box>
    </Container>
  );
}
